import React, { useState, useEffect, useRef } from "react";
import Button from "../customComponents/Button";
import Table from "../customComponents/Table";
import { createColumnHelper } from "@tanstack/react-table";
import { post } from "../utils/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import ConfirmationModal from "../customComponents/ConfirmationModal";
import DatePicker from "../customComponents/DatePicker";
import AddonModal from "../customComponents/AddonModal";
import { ToastContainer, toast } from "react-toastify";
import LoaderComponent from "./LoaderComponent";

const Addons = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const searchText = searchParams.get("search") || "";

  const updateParams = (updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v) next.set(k, v);
        else next.delete(k);
      });
      return next;
    });
  };

  const [localSearch, setLocalSearch] = useState(searchText);
  const searchTimerRef = useRef(null);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [tableData, setTableData] = useState([]);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));
  const [isLoading, setIsLoading] = useState(false);
  const [noOfPages, setNoOfPages] = useState(1);

  const columnHelper = createColumnHelper();

  const seasonalAddons = [
    "Sightseeing",
    "1 time Snacks",
    "2 time Snacks",
    "Gift",
    "Extra Breakfast",
    "Extra Lunch",
    "Extra Dinner",
    "Insurance",
    "DMC",
    "Visa",
  ];

  const columns = [
    columnHelper.accessor("slNo", {
      header: "Sl. No",
      cell: (info) => info.row.index + 1,
    }),
    columnHelper.accessor("addonitem", {
      header: "ADDON ITEM",
    }),
    columnHelper.accessor("itemname", {
      header: "ITEM NAME",
    }),
    columnHelper.accessor(
      (row) => {
        const isSeasonalAddon = seasonalAddons.includes(row.addonitem || "");
        const addonType = isSeasonalAddon ? "pph" : "tp";
        return addonType === "tp"
          ? `${row.totalprice} (Total Price)`
          : `${row.addonprice} (Price per Head)`;
      },
      {
        header: "PRICE PER HEAD / TOTAL PRICE",
        id: "priceperhead",
        cell: (info) => `₹ ${info.getValue()}`,
      }
    ),
    columnHelper.accessor("actions", {
      header: "ACTIONS",
      cell: (info) => (
        <div className="flex gap-2">
          {(user?.role === "Admin" || user?.role === "Backend Team Manager") && (
            <button
              className="p-2 bg-gray-200 rounded-md"
              onClick={() => {
                localStorage.setItem("addonid", info.row.original.addonid);
                navigate("/add-addons", { state: { editData: info.row.original } });
              }}
            >
              <span className="material-symbols-outlined">edit</span>
            </button>
          )}
          <button
            className="p-2 bg-gray-200 rounded-md"
            onClick={() => {
              setSelectedAddon(info.row.original);
              setShowVisibilityModal(true);
            }}
          >
            <span className="material-symbols-outlined">visibility</span>
          </button>
          {(user?.role === "Admin" || user?.role === "Backend Team Manager") && (
            <button
              className="p-2 bg-gray-200 text-red-500 rounded-md"
              onClick={() => handleDeleteClick(info.row.original)}
            >
              <span className="material-symbols-outlined">delete</span>
            </button>
          )}
        </div>
      ),
    }),
  ];

  useEffect(() => {
    fetchAddons();
  }, [currentPage, searchText]);

  const fetchAddons = async () => {
    setIsLoading(true);
    try {
      const response = await post("/getAllAddons", {
        page: currentPage,
        limit: 10,
        search: searchText.trim(),
      });
      const temp = response.data.map((list) => ({
        alterCreatedon: new Date(list.createdon)
          .toLocaleDateString("en-GB")
          .replace(/\//g, "-"),
        ...list,
      }));
      setNoOfPages(response?.pagination?.total);
      setTableData(temp);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching addons:", error);
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      updateParams({ search: value || null, page: null });
    }, 400);
  };

  const handleDeleteClick = (item) => {
    setDeleteItem(item);
    setIsDeleteModal(true);
  };

  const handleCloseModal = () => {
    setIsDeleteModal(false);
    setDeleteItem(null);
  };

  const handleConfirmDelete = async () => {
    try {
      await post("/deleteAddon", { addonid: deleteItem.addonid });
      toast.success("Addon deleted successfully");
      fetchAddons();
      handleCloseModal();
    } catch (error) {
      toast.error("Addon deleted failed");
      console.error("Error deleting addon:", error);
    }
  };

  const handleDateChange = (filterDate) => {
    setSelectedDate(filterDate);
  };

  return (
    <div>
      <div className="flex justify-between">
        <div>
          <h2 className="text-primary-black text-[28px] font-semibold">
            Addons
          </h2>
          <p className="text-[14px]">
            <span className="font-bold">Addons</span> /
            <span className="font-regular">Addons List</span>
          </p>
        </div>
        <div className="flex mt-4 justify-end gap-4">
          <Button
            type="primary"
            label="Add Addons"
            onClick={() => navigate("/add-addons")}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 mt-4 md:flex md:justify-between">
        <div className="w-full">
          <input
            type="text"
            className="outline-none p-2 rounded-l-lg w-full sm:w-5/12 h-[40px]"
            placeholder="Search..."
            value={localSearch}
            onChange={handleSearch}
          />
        </div>
        <div className="w-full md:w-auto md:flex md:gap-4">
          <div>
            <DatePicker
              placeholder="Select Destination..."
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
            />
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isDeleteModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        title="Delete Addon"
        message={`Are you sure you want to delete the Addon "${deleteItem?.addonitem}"?`}
      />

      <div className="bg-white rounded-xl border-gray-300 mt-7 pb-6">
        {isLoading ? (
          <LoaderComponent />
        ) : (
          <Table
            columns={columns}
            data={tableData}
            noOfPages={noOfPages}
            currentPage={currentPage}
            setCurrentPage={(page) => updateParams({ page: page > 1 ? String(page) : null })}
          />
        )}
      </div>

      {showVisibilityModal && (
        <AddonModal
          onClose={() => setShowVisibilityModal(false)}
          addonData={selectedAddon || {}}
        />
      )}
      <ToastContainer />
    </div>
  );
};

export default Addons;
